import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { EmojiButton } from '../../components/ui/EmojiPicker';
import { api } from '../../lib/api';
import type { Profile } from './types';

/**
 * What somebody is feeling, in a speech bubble over their avatar.
 *
 * ## Why a bubble and not a line of text
 *
 * The profile already has a bio, a location and a set of tags - a fourth line
 * of prose would read as more of the same and be skipped with the rest. A
 * comic bubble with a tail pointing at the face is the one shape that says
 * "they are saying this, now" rather than "this is a field about them", which
 * is the entire difference between a mood and a bio.
 *
 * ## Edited in place, because that is what it looks like it does
 *
 * It is a small thing you press and change. Sending somebody to settings, or
 * opening a dialog over the page, would both be more machinery than the thing
 * is worth - and neither would let them see the bubble they are writing sitting
 * where it will sit. The bubble becomes the editor and goes back to being a
 * bubble.
 *
 * ## Only yours is pressable
 *
 * On anybody else's profile it is a bubble and nothing more: no cursor, no
 * hover, nothing to click. And when they have not set one there is nothing at
 * all - an empty prompt on a stranger's profile would be asking a visitor to
 * fill in someone else's mood.
 */
export function MoodBubble({ profile, isOwner }: { profile: Profile; isOwner: boolean }) {
  const [editing, setEditing] = useState(false);

  const mood = profile.mood ?? null;

  if (editing) {
    return (
      <Editor mood={mood} onDone={() => setEditing(false)} />
    );
  }

  if (!mood) {
    // Nothing to show, and nothing to offer somebody it does not belong to.
    if (!isOwner) return null;

    return (
      <Bubble dashed>
        <button
          type="button"
          data-mood-set
          onClick={() => setEditing(true)}
          className="meta whitespace-nowrap text-faint transition-colors hover:text-soft"
        >
          Set your mood
        </button>
      </Bubble>
    );
  }

  const content = (
    <>
      {mood.emoji && (
        <span className="text-base leading-none" aria-hidden>
          {mood.emoji}
        </span>
      )}
      {mood.text && <span className="truncate text-sm text-ink">{mood.text}</span>}
    </>
  );

  if (!isOwner) {
    return (
      <Bubble>
        <span data-mood className="flex items-center gap-1.5">
          {content}
        </span>
      </Bubble>
    );
  }

  return (
    <Bubble>
      <button
        type="button"
        data-mood
        data-mood-own
        title="Change your mood"
        onClick={() => setEditing(true)}
        className="flex items-center gap-1.5 transition-opacity hover:opacity-70"
      >
        {content}
      </button>
    </Bubble>
  );
}

function Editor({
  mood,
  onDone,
}: {
  mood: { emoji: string | null; text: string | null } | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [emoji, setEmoji] = useState(mood?.emoji ?? '');
  const [text, setText] = useState(mood?.text ?? '');
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
  }, []);

  const save = useMutation({
    mutationFn: (next: { emoji: string; text: string } | null) =>
      api.patch('/me', { mood: next }),
    onSuccess: async () => {
      /*
       * The profile query, not the session. The bubble is drawn from the
       * profile this page already loaded, so that is the copy that has to
       * change - refreshing the session would update nothing on screen.
       */
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      onDone();
    },
  });

  // A mood needs one of the two, so saving nothing is the same as clearing.
  const empty = emoji === '' && text.trim() === '';

  const commit = () => {
    if (save.isPending) return;

    save.mutate(empty ? null : { emoji, text });
  };

  return (
    <Bubble>
      <div
        data-mood-editor
        className="flex items-center gap-1.5"
        onKeyDown={(event) => {
          if (event.key === 'Escape') onDone();
        }}
      >
        {/*
          The emoji sits left of the words, which is the order it is read in
          and the order it is written in.
        */}
        <EmojiButton
          direction="down"
          align="left"
          closeOnSelect
          onSelect={(char) => setEmoji(char)}
        >
          <span
            data-mood-emoji={emoji || undefined}
            className={`flex h-7 w-7 items-center justify-center rounded-[var(--radius-pill)] text-base leading-none transition-colors hover:bg-app ${
              emoji ? '' : 'text-ghost'
            }`}
          >
            {emoji || '☺'}
          </span>
        </EmojiButton>

        <input
          ref={input}
          value={text}
          maxLength={60}
          placeholder="How are you feeling?"
          data-mood-input
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
          }}
          className="w-44 min-w-0 bg-transparent text-sm text-ink outline-none placeholder:text-ghost"
        />

        <button
          type="button"
          data-mood-save
          disabled={save.isPending}
          onClick={commit}
          className="meta-caps shrink-0 rounded-[var(--radius-pill)] bg-accent px-2 py-1 text-accent-ink transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          Save
        </button>

        {/*
          A button of its own, and only when there is something to clear.

          Emptying the text is not enough once an emoji has been picked - there
          is no way to un-pick one - so without this a mood could be changed
          forever but never taken down, which is half of what the thing is for.
        */}
        {mood && (
          <button
            type="button"
            data-mood-clear
            disabled={save.isPending}
            onClick={() => {
              if (!save.isPending) save.mutate(null);
            }}
            className="meta-caps shrink-0 rounded-[var(--radius-pill)] px-1.5 py-1 text-faint transition-colors hover:text-soft disabled:opacity-50"
          >
            Clear
          </button>
        )}
      </div>
    </Bubble>
  );
}

/**
 * The comic shape.
 *
 * The tail is a rotated square rather than a border triangle so it can carry
 * the same ring as the body, and it is painted *under* the body - `z-[1]` on
 * the panel - so the two edges that would cross the bubble's own outline are
 * covered and what is left is a continuous line around one shape.
 */
function Bubble({ children, dashed = false }: { children: React.ReactNode; dashed?: boolean }) {
  const ring = dashed
    ? 'border border-dashed border-line-strong'
    : 'ring-1 ring-line';

  return (
    <div data-mood-bubble className="relative w-max max-w-[min(20rem,60vw)]">
      <div className={`relative z-[1] rounded-2xl bg-raised px-3 py-1.5 shadow-lg ${ring}`}>
        {children}
      </div>

      {/*
        Offset left rather than centred: it points at the avatar below, and the
        avatar is at the left edge of the row rather than under the middle of a
        bubble whose width depends on what somebody typed.
      */}
      <div
        aria-hidden
        className={`absolute -bottom-1 left-5 h-3 w-3 rotate-45 bg-raised ${
          dashed ? 'border-b border-r border-dashed border-line-strong' : 'ring-1 ring-line'
        }`}
      />
    </div>
  );
}
